import React, { useMemo } from "react";
import { Box, MenuItem } from "@mui/material";
import { DeleteOutlined, PictureAsPdfOutlined } from "@mui/icons-material";
import colors from "../../../shared/themes/colors";
import useTranslation from "../../../shared/hooks/useTranslation";
import { useAppSelector } from "../../../shared/hooks/sharedHooks";
import { StatusChip } from "../../../shared/components/chips/StatusChip";
import TableActionButton from "../../../shared/components/tables/TableActionButton";
import { ServiceRequest } from "../models/models";
import { ServiceReportsState } from "../../machinery/models/ServiceReportsModel";
import { createStatusColorMap, createStatusLabelMap } from "../utils/statusMaps";
import { useServiceRequestFormatters } from "../hooks/useServiceRequestFormatters";
import AffectedPartCategoryChip from "./shared/AffectedPartCategoryChip";
import PriorityChip from "./shared/PriorityChip";
import ViewServiceRequestSectionCard from "./ViewServiceRequestSectionCard";
import PermissionGuard from "../../../shared/components/PermissionGuard";
import { Modules, PermissionLevel } from "../../../core/models/PermissionModels";

export interface ViewServiceRequestGeneralInfoCardProps {
  serviceRequest: ServiceRequest;
  onDownloadPdf?: () => void;
  onDelete?: () => void;
}

const ViewServiceRequestGeneralInfoCard = ({
  serviceRequest, onDownloadPdf, onDelete
}: ViewServiceRequestGeneralInfoCardProps) => {
  const { translate } = useTranslation(["myMachinery"]);
  const { machineParts } = useAppSelector<ServiceReportsState>(
    (state: any) => state.customerServiceReports
  );
  const partName = useMemo(() => {
    if (!serviceRequest.affectedPartCategoryId || !machineParts) return undefined;
    for (const group of machineParts) {
      const found = group.parts.find(p => p.id === serviceRequest.affectedPartCategoryId);
      if (found) return found.name;
    }
    return undefined;
  }, [machineParts, serviceRequest.affectedPartCategoryId]);

  const actionsAvailable = !!onDownloadPdf || !!onDelete;

  const {
    formatCreatedDate,
    formatExpirationDate,
    formatPostedDate,
    formatServiceType,
    formatServiceDate,
  } = useServiceRequestFormatters();

  const terms = [
    { label: "servicing:created", value: formatCreatedDate(serviceRequest) },
    { label: "servicing:serviceType", value: formatServiceType(serviceRequest) },
    { label: "servicing:component", value: partName },
    { label: "servicing:serviceDate", value: formatServiceDate(serviceRequest) },
  ];

  return (
    <ViewServiceRequestSectionCard>
      <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <Box className="text-18-bold" color={colors.secondary.gray20}>
            {translate("servicing:request")} {serviceRequest.internalNumber}
          </Box>

          <Box sx={{ display: "flex", alignItems: "center", gap: 3 }}>
            <Box className="text-14-medium" color={colors.primary.red}>
              {formatExpirationDate(serviceRequest)}
            </Box>

            <Box className="text-14-medium" color={colors.secondary.gray20}>
              {formatPostedDate(serviceRequest)}
            </Box>

            {actionsAvailable && (
              <TableActionButton>
                <MenuItem onClick={() => onDownloadPdf?.()}>
                  <PictureAsPdfOutlined />
                  {translate("downloadPdf")}
                </MenuItem>

                <PermissionGuard module={Modules.Servicing} level={PermissionLevel.Admin}>
                  <MenuItem onClick={() => onDelete?.()}>
                    <DeleteOutlined />
                    {translate("common:buttons.delete")}
                  </MenuItem>
                </PermissionGuard>
              </TableActionButton>
            )}
          </Box>
        </Box>

        <Box sx={{ display: "flex", gap: 1 }}>
          <PriorityChip value={serviceRequest.priorityTypeCode} />

          <StatusChip
            status={serviceRequest.statusCode}
            colorMap={createStatusColorMap()}
            labelMap={createStatusLabelMap(translate)}
          />
        </Box>

        <Box
          sx={{
            background: colors.secondary.gray98,
            borderRadius: "8px",
            display: "flex",
            gap: 3,
            p: 2,
            mt: 3
          }}
        >
          {terms?.map((termsItem) => (
            <Box key={termsItem.label} sx={{ flex: 1 }}>
              <Box className="text-12-regular" color={colors.secondary.gray40}>
                {translate(termsItem.label)}
              </Box>

              <Box className="text-14-medium" color={colors.secondary.gray20}>
                {termsItem.value}
              </Box>
            </Box>
          ))}
        </Box>

        <Box sx={{ display: "flex", gap: 3, mt: 3 }}>
          <Box className="text-18-medium" color={colors.secondary.gray20}>
            {serviceRequest.issue}
          </Box>

          <Box>
            <AffectedPartCategoryChip value={partName} />
          </Box>
        </Box>

        <Box sx={{ display: "flex", flexDirection: "column", gap: 1, mt: 2 }}>
          <Box className="text-14-medium" color={colors.secondary.gray40}>
            {translate("servicing:reportDescription")}
          </Box>

          <Box className="text-16-regular" color={colors.secondary.gray20}>
            {serviceRequest.description}
          </Box>
        </Box>
      </Box>
    </ViewServiceRequestSectionCard>
  );
};

export default ViewServiceRequestGeneralInfoCard;
